import React, { useState, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Button,
  IconButton,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  useTheme,
  alpha,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Tooltip,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
  styled,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  GridView as GridViewIcon,
  List as ListViewIcon,
  Search as SearchIcon,
  InsertDriveFile as InsertDriveFileIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { Document as DocumentType, Permit } from '../../types';
import { format } from 'date-fns';
import { documentAPI } from '../../apis';

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
}));

const FilterButton = styled(Button)(({ theme }) => ({
  borderRadius: '16px',
  padding: '2px 8px',
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '0.75rem',
  color: theme.palette.text.primary,
  backgroundColor: '#F2F2F2',
  '&.selected': {
    backgroundColor: '#2A2C40',
    color: '#ffffff',
    '&:hover': {
      backgroundColor: '#2A2C40',
    },
  },
  '&:hover': {
    backgroundColor: alpha('#F2F2F2', 0.8),
  },
  margin: '0 1px',
}));

const ActionButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.secondary,
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
  },
}));

// Document List Dialog Component
const DocumentListDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  documents: Array<DocumentType & { permitType: string }>;
  onDocumentClick: (document: DocumentType) => void;
  onReupload: (document: DocumentType) => void;
  onDelete: (document: DocumentType) => void;
  error: string | null;
  isSubmitting: boolean;
}> = ({ open, onClose, documents, onDocumentClick, onReupload, onDelete, error, isSubmitting }) => {
  const theme = useTheme();
  
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          position: 'fixed',
          top: '64px',
          right: '24px',
          m: 0,
          height: 'calc(100% - 80px)',
          maxHeight: 'none',
          width: '400px',
          maxWidth: '90%',
          borderRadius: '16px',
          boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
        }
      }}
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'transparent'
        }
      }}
    >
      <DialogTitle sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1" fontWeight={600}>All Documents ({documents.length})</Typography>
          <IconButton onClick={onClose} size="small" sx={{ color: 'text.secondary' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 2, overflow: 'auto' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <List sx={{ width: '100%' }}>
          {documents.map((document) => (
            <ListItem
              key={document.documentID}
              disablePadding
              sx={{
                mb: 1,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden',
                bgcolor: 'background.paper',
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                },
              }}
            >
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                width: '100%',
                p: 1.5,
                gap: 1.5
              }}>
                <InsertDriveFileIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                <Box 
                  onClick={() => onDocumentClick(document)}
                  sx={{ 
                    flex: 1,
                    minWidth: 0,
                    cursor: 'pointer',
                  }}
                >
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {document.name}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{
                      display: 'block',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {document.permitType} • Version {document.version}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Tooltip title="Reupload">
                    <IconButton 
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onReupload(document);
                      }}
                      disabled={isSubmitting}
                      sx={{ 
                        color: theme.palette.primary.main,
                        p: 0.5,
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.08),
                        },
                      }}
                    >
                      {isSubmitting ? (
                        <CircularProgress size={18} />
                      ) : (
                        <CloudUploadIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton 
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(document);
                      }}
                      disabled={isSubmitting}
                      sx={{ 
                        color: theme.palette.error.main,
                        p: 0.5,
                        '&:hover': {
                          bgcolor: alpha(theme.palette.error.main, 0.08),
                        },
                      }}
                    >
                      {isSubmitting ? (
                        <CircularProgress size={18} />
                      ) : (
                        <DeleteIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </ListItem>
          ))}
        </List>
      </DialogContent>
    </Dialog>
  );
};

interface ProjectDocumentsProps {
  permits: Permit[];
  handleDocumentClick: (document: DocumentType) => void;
  setIsCreatePermitDialogOpen: (open: boolean) => void;
  setPermits: React.Dispatch<React.SetStateAction<Permit[]>>;
}

const ProjectDocuments: React.FC<ProjectDocumentsProps> = ({
  permits,
  handleDocumentClick,
  setIsCreatePermitDialogOpen,
  setPermits,
}) => {
  const theme = useTheme();
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPermitType, setSelectedPermitType] = useState<string>('All');
  const [isDocumentListDialogOpen, setIsDocumentListDialogOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const totalDocuments = permits.reduce((acc, permit) => acc + (permit.documents?.length || 0), 0);

  const getFileIcon = (fileType: string) => {
    if (fileType.includes('image')) return <ImageIcon sx={{ color: theme.palette.primary.main }} />;
    if (fileType.includes('pdf')) return <PdfIcon sx={{ color: theme.palette.primary.main }} />;
    return <InsertDriveFileIcon sx={{ color: theme.palette.primary.main }} />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const getCategoryChip = (category: string) => {
    return (
      <Chip
        label={category}
        sx={{
          backgroundColor: '#2A2C40',
          borderRadius: '16px',
          color: '#FFFFFF',
          fontWeight: 500,
        }}
        size="small"
      />
    );
  };

  const getStatusChip = (status: string) => {
    const statusConfig = {
      approved: { color: 'success' as const, icon: <CheckCircleIcon fontSize="small" /> },
      rejected: { color: 'error' as const, icon: <CancelIcon fontSize="small" /> },
      pending: { color: 'warning' as const, icon: <WarningIcon fontSize="small" /> },
    };
    const config = statusConfig[status.toLowerCase() as keyof typeof statusConfig] || statusConfig.pending;
    return (
      <Chip
        size="small"
        icon={config.icon}
        label={status.charAt(0).toUpperCase() + status.slice(1)}
        color={config.color}
        sx={{ 
          borderRadius: '16px',
          '& .MuiChip-icon': {
            color: 'inherit',
          },
        }}
      />
    );
  };

  const handleReuploadDocument = async (document: DocumentType) => {
    try {
      setIsSubmitting(true);
      setError(null);
      
      const input = window.document.createElement('input');
      input.type = 'file';
      input.accept = '.pdf,.doc,.docx,.txt,.dwg,.dxf,.jpg,.jpeg,.png';
      
      input.onchange = async (e: Event) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (!file) return;
        
        try {
          const response = await documentAPI.updateDocument(document.documentID, {
            file: file
          });
          
          if (response.status === 'success') {
            // Update the permits state to reflect the new document version
            setPermits(prevPermits => 
              prevPermits.map(permit => ({
                ...permit,
                documents: permit.documents?.map(doc => 
                  doc.documentID === document.documentID 
                    ? { 
                        ...doc, 
                        ...response.data,
                        uploadDate: new Date(response.data.uploadDate)
                      }
                    : doc
                ) || []
              }))
            );
          }
        } catch (error) {
          console.error('Error updating document:', error);
          setError('Failed to update document. Please try again.');
        }
      };
      
      input.click();
    } catch (error) {
      console.error('Error handling reupload:', error);
      setError('Failed to initiate reupload. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleDeleteDocument = async (document: DocumentType) => {
    try {
      if (!window.confirm('Are you sure you want to delete this document? This action cannot be undone.')) {
        return;
      }
      
      setIsSubmitting(true);
      setError(null);
      
      const response = await documentAPI.deleteDocument(document.documentID);
      
      if (response.status === 'success') {
        // Update the permits list by filtering out the deleted document
        setPermits(prevPermits => prevPermits.map(permit => ({
          ...permit,
          documents: permit.documents?.filter(doc => doc.documentID !== document.documentID)
        })));
      } else {
        throw new Error(response.message || 'Failed to delete document');
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      setError('Failed to delete document. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleViewDocument = async (document: DocumentType) => {
    try {
      const response = await documentAPI.getDocument(document.documentID);
      if (response.status === 'success' && response.data.signedUrl) {
        // Check if the signedUrl is valid
        if (!response.data.signedUrl.startsWith('http')) {
          throw new Error('Invalid document URL received');
        }
        window.open(response.data.signedUrl, '_blank', 'noopener,noreferrer');
      } else {
        setError('Failed to get document URL. Please try again.');
        console.error('Failed to get document URL:', response);
      }
    } catch (error) {
      console.error('Error viewing document:', error);
      setError(error instanceof Error ? error.message : 'Failed to view document. Please try again.');
    }
  };

  const filteredDocuments = permits.flatMap(permit =>
    (permit.documents || [])
      .filter(doc => {
        if (selectedPermitType !== 'All' && permit.permitType !== selectedPermitType) return false;
        if (searchQuery) {
          const query = searchQuery.toLowerCase();
          return doc.name.toLowerCase().includes(query) ||
                 doc.type.toLowerCase().includes(query) ||
                 permit.permitType.toLowerCase().includes(query);
        }
        return true;
      })
      .map(doc => ({
        ...doc,
        permitType: permit.permitType,
        projectID: permit.projectID
      }))
  );

  return (
    <Paper
      sx={{
        borderRadius: '16px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
        overflow: 'hidden',
        mb: 4
      }}
    >
      <Box sx={{
        p: 3,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <Typography variant="h5" fontWeight="600">Documents</Typography>
        {filteredDocuments.length > 0 && (
          <Button
            variant="contained"
            startIcon={<CloudUploadIcon />}
            sx={{
              borderRadius: '30px',
              textTransform: 'none',
              px: 3,
              py: 1.2,
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              bgcolor: '#4169E1',
              '&:hover': {
                bgcolor: '#3457cd',
              },
              transition: 'all 0.2s ease',
              fontWeight: 600,
            }}
            onClick={() => setIsCreatePermitDialogOpen(true)}
          >
            Upload New Files
          </Button>
        )}
      </Box>

      <Box sx={{
        px: 3,
        pb: 3,
      }}>
        {/* Search Bar */}
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search documents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Filter Buttons
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.25, flexWrap: 'wrap' }}>
          <FilterButton
            key="All"
            onClick={() => setSelectedPermitType('All')}
            className={selectedPermitType === 'All' ? 'selected' : ''}
            disableElevation
          >
            All
          </FilterButton>
          {['Building Permit', 'Electrical Permit', 'Plumbing Permit', 'Mechanical Permit', 'Demolition Permit', 'Grading Permit', 'Site Development Permit', 'Other'].map((type) => (
            <FilterButton
              key={type}
              onClick={() => setSelectedPermitType(type)}
              className={selectedPermitType === type ? 'selected' : ''}
              disableElevation
            >
              {type}
            </FilterButton>
          ))}
        </Box> */}
      </Box>

      {filteredDocuments.length > 0 ? (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: 'text.secondary', fontWeight: 500 }}>Document Name</TableCell>
              <TableCell sx={{ color: 'text.secondary', fontWeight: 500 }}>Version</TableCell>
              <TableCell sx={{ color: 'text.secondary', fontWeight: 500 }}>File Category</TableCell>
              <TableCell sx={{ color: 'text.secondary', fontWeight: 500 }}>Upload Date</TableCell>
              <TableCell sx={{ color: 'text.secondary', fontWeight: 500 }}>Last Updated</TableCell>
              <TableCell align="right"></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredDocuments.map((document) => (
              <StyledTableRow 
                key={document.documentID} 
                onClick={() => handleDocumentClick(document)}
              >
                <TableCell>
                  <Typography fontWeight={600}>{document.name}</Typography>
                </TableCell>
                <TableCell>
                  {document?.version ?? '1.0'}
                </TableCell>
                <TableCell>
                  {getCategoryChip(document.permitType)}
                </TableCell>
                <TableCell>
                  {new Date(document.uploadDate).toLocaleString('en-US', {
                    year: 'numeric',
                    month: 'numeric',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: 'numeric',
                    hour12: true
                  })}
                </TableCell>
                <TableCell>
                  {new Date(document.uploadDate).toLocaleString('en-US', {
                    year: 'numeric',
                    month: 'numeric',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: 'numeric',
                    hour12: true
                  })}
                </TableCell>
                <TableCell align="right">
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Tooltip title="View">
                      <ActionButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewDocument(document);
                        }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </ActionButton>
                    </Tooltip>
                    <Tooltip title="Reupload">
                      <ActionButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleReuploadDocument(document);
                        }}
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <CircularProgress size={20} />
                        ) : (
                          <CloudUploadIcon fontSize="small" />
                        )}
                      </ActionButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <ActionButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteDocument(document);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </ActionButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            No documents found
          </Typography>
          <Button
            variant="contained"
            startIcon={<CloudUploadIcon />}
            sx={{
              mt: 2,
              borderRadius: '30px',
              textTransform: 'none',
              bgcolor: '#4169E1',
            }}
            onClick={() => setIsCreatePermitDialogOpen(true)}
          >
            Upload Documents
          </Button>
        </Box>
      )}

      {/* Document List Dialog */}
      <DocumentListDialog
        open={isDocumentListDialogOpen}
        onClose={() => setIsDocumentListDialogOpen(false)}
        documents={filteredDocuments}
        onDocumentClick={handleDocumentClick}
        onReupload={handleReuploadDocument}
        onDelete={handleDeleteDocument}
        error={error}
        isSubmitting={isSubmitting}
      />
    </Paper>
  );
};

export default ProjectDocuments; 